import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom'
import { QueryClientProvider } from '@tanstack/react-query'
import { queryClient } from '@/lib/query-client'
import { Toaster } from '@/components/ui/toaster'
import { AppLayout } from '@/components/layout'

// Auth pages
import { LoginPage, RegisterPage } from '@/features/auth'

// Feature pages
import { DashboardPage, AnalyticsDashboardPage } from '@/features/dashboard'
import { DomainsPage } from '@/features/domains'
import { MonitorsPage } from '@/features/monitors'
import { DnsPage } from '@/features/dns'
import { SslPage } from '@/features/ssl'
import { SecretsPage } from '@/features/secrets'
import { StatusPagesPage } from '@/features/status-pages'
import { AlertingPage } from '@/features/alerting'
import { SettingsPage } from '@/features/settings'

// New module pages
import { EmailSecurityPage } from '@/features/email-security'
import { AuditLogPage } from '@/features/audit-log'
import { ChangeManagementPage } from '@/features/change-management'
import { IpamPage } from '@/features/ipam'
import { VendorsPage } from '@/features/vendors'
import { TrainingPage } from '@/features/training'
import { CsatPage } from '@/features/csat'
import { PostMortemPage } from '@/features/post-mortem'

// Wave 2 module pages
import { ReportingPage } from '@/features/reporting'
import { CveFeedPage } from '@/features/cve-feed'
import { VendorRiskPage } from '@/features/vendor-risk'
import { PlatformStatusPage } from '@/features/platform-status'

// Wave 3 module pages
import { ItToolsPage } from '@/features/it-tools'
import { ApiHubPage } from '@/features/api-hub'
import { ContractsPage } from '@/features/contracts'
import { CyberInsurancePage } from '@/features/cyber-insurance'
import { OnboardingPage } from '@/features/onboarding'
import { PlaybooksPage } from '@/features/playbooks'
import { OffboardingPage } from '@/features/offboarding'
import { WordPressPage } from '@/features/wordpress'
import { SpaceBookingPage } from '@/features/space-booking'

// Wave 4 module pages
import { TicketsPage } from '@/features/tickets'
import { InfrastructurePage } from '@/features/infrastructure'
import { DevopsPage } from '@/features/devops'
import { AttackSurfacePage } from '@/features/attack-surface'
import { TimesheetsPage } from '@/features/timesheets'
import { RunbooksPage } from '@/features/runbooks'
import { ClientPortalPage } from '@/features/client-portal'

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <BrowserRouter>
        <Routes>
          {/* Public routes */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />

          {/* Protected routes */}
          <Route element={<AppLayout />}>
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/analytics" element={<AnalyticsDashboardPage />} />
            <Route path="/domains" element={<DomainsPage />} />
            <Route path="/monitors" element={<MonitorsPage />} />
            <Route path="/dns" element={<DnsPage />} />
            <Route path="/ssl" element={<SslPage />} />
            <Route path="/secrets" element={<SecretsPage />} />
            <Route path="/status-pages" element={<StatusPagesPage />} />
            <Route path="/alerting" element={<AlertingPage />} />
            <Route path="/api-keys" element={<SettingsPage />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/settings/*" element={<SettingsPage />} />

            {/* New modules */}
            <Route path="/email-security" element={<EmailSecurityPage />} />
            <Route path="/audit-log" element={<AuditLogPage />} />
            <Route path="/changes" element={<ChangeManagementPage />} />
            <Route path="/ipam" element={<IpamPage />} />
            <Route path="/vendors" element={<VendorsPage />} />
            <Route path="/training" element={<TrainingPage />} />
            <Route path="/csat" element={<CsatPage />} />
            <Route path="/post-mortems" element={<PostMortemPage />} />

            {/* Wave 2 modules */}
            <Route path="/reports" element={<ReportingPage />} />
            <Route path="/cve-feed" element={<CveFeedPage />} />
            <Route path="/vendor-risk" element={<VendorRiskPage />} />
            <Route path="/platform-status" element={<PlatformStatusPage />} />

            {/* Wave 3 modules */}
            <Route path="/it-tools" element={<ItToolsPage />} />
            <Route path="/api-hub" element={<ApiHubPage />} />
            <Route path="/onboarding" element={<OnboardingPage />} />
            <Route path="/playbooks" element={<PlaybooksPage />} />
            <Route path="/contracts" element={<ContractsPage />} />
            <Route path="/cyber-insurance" element={<CyberInsurancePage />} />
            <Route path="/offboarding" element={<OffboardingPage />} />
            <Route path="/wordpress" element={<WordPressPage />} />
            <Route path="/space-booking" element={<SpaceBookingPage />} />

            {/* Wave 4 modules */}
            <Route path="/tickets" element={<TicketsPage />} />
            <Route path="/infrastructure" element={<InfrastructurePage />} />
            <Route path="/devops" element={<DevopsPage />} />
            <Route path="/attack-surface" element={<AttackSurfacePage />} />
            <Route path="/timesheets" element={<TimesheetsPage />} />
            <Route path="/runbooks" element={<RunbooksPage />} />
            <Route path="/client-portal" element={<ClientPortalPage />} />
          </Route>

          {/* Default redirect */}
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster />
    </QueryClientProvider>
  )
}
